import { useCallback, useEffect, useState } from 'react';
import { getKlydoScrubVideoUrl } from '../../../utils/klydoThumbnail';

export type ScrubVideo = {
  loopUrl: string;
  url: string;
  isLocalCopy: boolean;
};

// The scrub copy is downloaded whole before it plays. On its first request
// Cloudinary streams it while it transcodes, so a <video> reads a partial
// duration and range requests for the rest fail with 503.
const useScrubVideo = ({
  open,
  loopUrl,
}: {
  open: boolean;
  loopUrl: string;
}) => {
  const [scrubVideo, setScrubVideo] = useState<ScrubVideo>();
  const loadedLoopUrl = scrubVideo?.loopUrl;

  useEffect(() => {
    if (!open || loadedLoopUrl === loopUrl) return;

    const controller = new AbortController();
    fetch(getKlydoScrubVideoUrl(loopUrl), { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Scrub video request failed with ${response.status}`);
        }
        return response.blob();
      })
      .then((blob) =>
        setScrubVideo({
          loopUrl,
          url: URL.createObjectURL(blob),
          isLocalCopy: true,
        }),
      )
      .catch(() => {
        if (controller.signal.aborted) return;
        setScrubVideo({ loopUrl, url: loopUrl, isLocalCopy: false });
      });

    return () => controller.abort();
  }, [open, loopUrl, loadedLoopUrl]);

  useEffect(
    () => () => {
      if (scrubVideo?.isLocalCopy) URL.revokeObjectURL(scrubVideo.url);
    },
    [scrubVideo],
  );

  const handleVideoError = useCallback(
    () => setScrubVideo({ loopUrl, url: loopUrl, isLocalCopy: false }),
    [loopUrl],
  );

  return {
    scrubVideo: loadedLoopUrl === loopUrl ? scrubVideo : undefined,
    handleVideoError,
  };
};

export default useScrubVideo;
